import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAuth, type Auth, GoogleAuthProvider } from 'firebase/auth';
import { getFirestore, initializeFirestore, type Firestore } from 'firebase/firestore';
import { getFunctions, type Functions } from 'firebase/functions';
import { getStorage, type FirebaseStorage } from 'firebase/storage';

const envConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID
};

const isConfigured = Boolean(envConfig.apiKey && envConfig.projectId);

// 빈 env 로 initializeApp 을 호출하면 일부 SDK 검증 경로에서 모듈 로드 자체가
// 실패하거나, 첫 렌더 도중 throw 가 발생해 UI 가 흰 화면이 된다.
// 따라서 .env 가 비어있을 때는 합법적 형태의 placeholder 로 초기화하여
// Firebase 모듈은 안전하게 로드되되, 실제 호출(로그인/Firestore)은 차단되도록 한다.
const placeholderConfig = {
  apiKey: 'placeholder-not-configured',
  authDomain: 'placeholder.firebaseapp.com',
  projectId: 'placeholder-project',
  storageBucket: 'placeholder.appspot.com',
  messagingSenderId: '000000000000',
  appId: '1:000000000000:web:0000000000000000000000'
};

const firebaseConfig = isConfigured ? envConfig : placeholderConfig;

if (!isConfigured) {
  console.warn(
    '[firebase] VITE_FIREBASE_* 환경변수가 비어있습니다. UI 미리보기는 가능하지만 로그인/Firestore 호출은 동작하지 않습니다. .env 또는 .env.local 에 Firebase 콘솔에서 받은 값을 채우세요.'
  );
}

export const firebaseApp: FirebaseApp = initializeApp(firebaseConfig);
export const auth: Auth = getAuth(firebaseApp);
// initializeFirestore (vs getFirestore) — undefined 필드 자동 제외 옵션 부여.
// 옛/신규 사건이 일부 optional 필드를 빈 채로 두는 패턴이라 write 마다 일일이 정리하지 않게.
export const db: Firestore = initializeFirestore(firebaseApp, {
  ignoreUndefinedProperties: true
});
// (firebase SDK 가 export 검증을 위해 getFirestore 도 한 번 호출하길 기대하는 케이스가 있어 명시 — db 와 동일 인스턴스 반환)
void getFirestore;
export const storage: FirebaseStorage = getStorage(firebaseApp);
// Cloud Functions — 저장공간 정리 등 admin 백엔드 작업용. region 은 functions 배포 region 과 일치해야 함.
export const functions: Functions = getFunctions(firebaseApp, 'asia-northeast3');
export const googleProvider = new GoogleAuthProvider();
export const isFirebaseConfigured = isConfigured;
